import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, throwError } from 'rxjs';
import { catchError, concatMap, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { BaseActionTypes } from '../../actions/base.actions';
import { getToken } from '../../selectors/base.selectors';
import { stringifyIfNotNullOrUndefined } from '../../../../helper/helper';
import {
    CustomerOptionsActions, CustomerOptionsActionTypes,
    DeleteCustomerOptionsResponse, GetCustomerOptionsResponse, PatchCustomerOptionsResponse,
    PostCustomerOptionsResponse,
} from '../../actions/mdqx/customer-options.actions';
import { HttpService } from '../../../services/http.service';
import { CustomerOptionsEntity } from '../../../entities/mdqx/customer-options.entity';
import { State } from '../../index';
import { environment } from '../../../../../environments/environment';

enum ErrorCodes {
    Get = 'Kundendetails Abrufen fehlgeschlagen',
    Post = 'Kundendetails Anlegen fehlgeschlagen',
    Patch = 'Kundendetails Bearbeiten fehlgeschlagen',
    Delete = 'Kundendetails Löschen fehlgeschlagen',
}

@Injectable()
export class CustomerOptionsEffects {

    get$ = createEffect(() => this.actions$.pipe(
        ofType(CustomerOptionsActionTypes.GetCustomerOptionsRequest),
        map(
            () => `
            {
                customerOptions {
                    ${CustomerOptionsEntity.GqlFields}
                }
            }`,
        ),
        withLatestFrom(this.store.select(getToken)),
        switchMap(([query, token]) =>
            this.api.graphQl({ query }, { token }, '', environment.mdqxGatewayApiUrl).pipe(
                map(res => res && res.customerOptions),
                concatMap(res =>
                    res
                        ? [
                              new GetCustomerOptionsResponse(
                                  res.map(
                                      customerOptions => new CustomerOptionsEntity(customerOptions),
                                  ),
                              ),
                          ]
                        : throwError('empty response'),
                ),
                catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: ErrorCodes.Get,
                        Err: err,
                        Caught: caught,
                    }
                }))),
            ),
        ),
    ));


    post$ = createEffect(() => this.actions$.pipe(
        ofType(CustomerOptionsActionTypes.PostCustomerOptionsRequest),
        map(
            ({ Payload }) => `
            mutation {
                createCustomerOption(
                    ${stringifyIfNotNullOrUndefined(Payload, 'BSNR')}
                    customerId: ${JSON.stringify(Payload.customerId)}
                    ${stringifyIfNotNullOrUndefined(Payload, 'federalState')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'IK')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'paymentTarget')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'practiceTypeId')}
                ) {
                    ${CustomerOptionsEntity.GqlFields}
                }
            }`,
        ),
        withLatestFrom(this.store.select(getToken)),
        concatMap(([query, token]) =>
            this.api.graphQl({ query }, { token }, '', environment.mdqxGatewayApiUrl).pipe(
                map(res => res && res.createCustomerOption),
                concatMap(res =>
                    res
                        ? [new PostCustomerOptionsResponse(new CustomerOptionsEntity(res))]
                        : throwError('empty response'),
                ),
                catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: ErrorCodes.Post,
                        Err: err,
                        Caught: caught,
                    }
                }))),
            ),
        ),
    ));


    patch$ = createEffect(() => this.actions$.pipe(
        ofType(CustomerOptionsActionTypes.PatchCustomerOptionsRequest),
        map(
            ({ Payload }) => `
            mutation {
                changeCustomerOption(
                    ${stringifyIfNotNullOrUndefined(Payload, 'BSNR')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'customerId')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'federalState')}
                    id: ${JSON.stringify(Payload.id)}
                    ${stringifyIfNotNullOrUndefined(Payload, 'IK')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'paymentTarget')}
                    ${stringifyIfNotNullOrUndefined(Payload, 'practiceTypeId')}
                ) {
                    ${CustomerOptionsEntity.GqlFields}
                }
            }`,
        ),
        withLatestFrom(this.store.select(getToken)),
        concatMap(([query, token]) =>
            this.api.graphQl({ query }, { token }, '', environment.mdqxGatewayApiUrl).pipe(
                map(res => res && res.changeCustomerOption),
                concatMap(res =>
                    res
                        ? [new PatchCustomerOptionsResponse(new CustomerOptionsEntity(res))]
                        : throwError('empty response'),
                ),
                catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                    Payload: {
                        ToasterMessage: ErrorCodes.Patch,
                        Err: err,
                        Caught: caught,
                    }
                }))),
            ),
        ),
    ));


    delete$ = createEffect(() => this.actions$.pipe(
        ofType(CustomerOptionsActionTypes.DeleteCustomerOptionsRequest),
        withLatestFrom(this.store.select(getToken)),
        concatMap(([{ Payload }, token]) =>
            this.api
                .graphQl(
                    {
                        query: `
                        mutation {
                            deleteCustomerOption(id: ${JSON.stringify(Payload.id)})
                        }`,
                    },
                    { token }, '', environment.mdqxGatewayApiUrl
                )
                .pipe(
                    map(res => res && res.deleteCustomerOption),
                    concatMap(res =>
                        res
                            ? [new DeleteCustomerOptionsResponse(Payload.id)]
                            : throwError('falsy response'),
                    ),
                    catchError((err, caught) => of(BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage: ErrorCodes.Delete,
                            Err: err,
                            Caught: caught,
                        }
                    }))),
                ),
        ),
    ));

    constructor(
        private actions$: Actions<CustomerOptionsActions>,
        private api: HttpService,
        private store: Store<State>,
    ) {}
}
